import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

export const PersonId = Schema.String;

/**
 * Person role enum for authorization.
 * - user: Default authenticated user
 * - moderator: Content moderation privileges
 * - admin: Full administrative access
 */
export const PersonRole = Schema.Literal("user", "moderator", "admin");
export type PersonRole = Schema.Schema.Type<typeof PersonRole>;

/**
 * Which brand's Clerk instance a person row is bound to. Mirrors the API brand
 * registry (`packages/api/src/config/brands.ts` `CLERK_BRANDS`): the product
 * brands plus the standalone `consinna` and `orcaloops` instances, which render
 * an existing product but keep their own Clerk application and user pool. One
 * instance = one user pool, and email is unique only within one.
 *
 * @since 1.0.0
 */
export const ClerkInstance = Schema.Literal("clawinstall", "flirtmachine", "consinna", "orcaloops");
export type ClerkInstance = Schema.Schema.Type<typeof ClerkInstance>;

/**
 * Person model representing a user in the system.
 *
 * Authentication is handled via Clerk:
 * - clerkId: Unique identifier from Clerk (format: user_xxxxxxxx), nullable for
 *   legacy users and for leads who have not signed up yet
 * - phone: Contact phone number — contact phone lives on the person, not the customer
 * - imageUrl: Profile image URL from Clerk
 * - roles: Authorization roles for access control
 *
 * @since 1.0.0
 */
export class Person extends Model.Class<Person>("Person")({
  id: Model.Generated(Schema.String),
  clerkId: Schema.NullOr(Schema.String),
  clerkInstance: ClerkInstance,
  name: Schema.NonEmptyTrimmedString,
  email: Schema.NonEmptyTrimmedString,
  verifiedEmail: Schema.NullOr(Schema.String),
  phone: Schema.NullOr(Schema.String),
  roles: Schema.Array(Schema.String),
  imageUrl: Schema.NullOr(Schema.String),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
